import { useCallback, useEffect, useState } from "react";

export interface CRECategoryScore {
  score: number;
  categoryId: string;
  categoryName: string;
  metricsTotal: number;
  metricsScored: number;
}

export interface ChartLocation {
  id: string;
  addressRaw: string;
  totalScore: number;
  confidence: string;
  dataCoverage: number;
  categoryScores: CRECategoryScore[];
}

export interface ChartData {
  summary: {
    avgScore: number;
    minScore: number;
    maxScore: number;
    scoredCount: number;
    locationCount: number;
  };
  locations: ChartLocation[];
  profileWeights: Record<string, number> | null;
  profileName: string | null;
}

export function useChartData(campaignId: string, profileId: string | null, refreshKey: number) {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey intentionally triggers re-fetch
  const fetchData = useCallback(async () => {
    if (!campaignId || !profileId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/chart-data`);
      if (!res.ok) throw new Error("Failed to load chart data");
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load chart data");
    } finally {
      setLoading(false);
    }
  }, [campaignId, profileId, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
